import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { BrandMark } from '@/components/brand/BrandMark';
import './LockScreen.css';

export function LockScreen() {
  const {
    unlockWithFaceId,
    loginWithCognito,
    faceIdLabel,
    canOfflineUnlock,
    lockMessage,
    user,
  } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const offline = typeof navigator !== 'undefined' && !navigator.onLine;

  const tryUnlock = async () => {
    setBusy(true);
    setError(null);
    try {
      await unlockWithFaceId();
    } catch (err) {
      const msg =
        err instanceof Error && err.message === 'offline_unlock_unavailable'
          ? 'Offline unlock needs a newer device security feature. You can still unlock with Face ID while online.'
          : err instanceof Error
            ? err.message
            : `Could not unlock with ${faceIdLabel}`;
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    // Auto-trigger Face ID on lock screen mount
    const id = window.setTimeout(() => {
      void tryUnlock();
    }, 350);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once on mount
  }, []);

  return (
    <div className="lock-screen" role="main">
      <div className="lock-screen__glow" aria-hidden />
      <div className="lock-screen__content">
        <BrandMark className="lock-screen__brand" size={56} />
        <h1 className="lock-screen__title">Balance</h1>
        {user?.displayName || user?.email ? (
          <p className="lock-screen__greeting">
            Welcome back{user.displayName ? `, ${user.displayName}` : ''}
          </p>
        ) : (
          <p className="lock-screen__greeting">Unlock to continue</p>
        )}

        <button
          type="button"
          className="lock-screen__face"
          onClick={() => void tryUnlock()}
          disabled={busy}
          aria-label={`Unlock with ${faceIdLabel}`}
        >
          <span className="lock-screen__face-ring" aria-hidden />
          <span className="lock-screen__face-icon" aria-hidden>
            <svg viewBox="0 0 64 64" width="48" height="48" fill="none">
              <path
                d="M18 14h-4a4 4 0 0 0-4 4v4M46 14h4a4 4 0 0 1 4 4v4M18 50h-4a4 4 0 0 1-4-4v-4M46 50h4a4 4 0 0 0 4-4v-4"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <circle cx="24" cy="28" r="2.5" fill="currentColor" />
              <circle cx="40" cy="28" r="2.5" fill="currentColor" />
              <path
                d="M26 40c2.2 2.4 5 3.6 8 3.6s5.8-1.2 8-3.6"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span className="lock-screen__face-label">
            {busy ? 'Looking…' : `Unlock with ${faceIdLabel}`}
          </span>
        </button>

        {(lockMessage || error) && (
          <p className="lock-screen__message" role="status">
            {error || lockMessage}
          </p>
        )}

        {offline && !canOfflineUnlock && !error && !lockMessage && (
          <p className="lock-screen__message" role="status">
            Offline unlock needs a newer device security feature. You can still unlock with Face
            ID while online.
          </p>
        )}

        <button
          type="button"
          className="lock-screen__google"
          onClick={loginWithCognito}
          disabled={busy || offline}
        >
          Use Google
        </button>
      </div>
    </div>
  );
}
